import {useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {CircleHelp} from 'lucide-react'

interface CardHelpProps {
  content: string
  className?: string
  label?: string
}

export function CardHelp({content, className = '', label = 'Entenda este indicador'}: CardHelpProps) {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({left: 12, top: 12, above: false})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const updatePosition = () => {
    const trigger = triggerRef.current
    if (!trigger) return

    const rect = trigger.getBoundingClientRect()
    const width = tooltipRef.current?.offsetWidth || 300
    const height = tooltipRef.current?.offsetHeight || 110
    const margin = 12
    const gap = 10
    const above = rect.top >= height + gap + margin

    setPosition({
      left: Math.min(
        window.innerWidth - width - margin,
        Math.max(margin, rect.right - width)
      ),
      top: above ? rect.top - height - gap : rect.bottom + gap,
      above,
    })
  }

  useLayoutEffect(() => {
    if (visible) updatePosition()
  }, [visible, content])

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        aria-label={label}
        aria-expanded={visible}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        onClick={(event) => event.stopPropagation()}
        className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-200 ${className}`}
      >
        <CircleHelp className='h-4 w-4' aria-hidden='true' />
      </button>

      {visible && typeof document !== 'undefined' && createPortal(
        <div
          ref={tooltipRef}
          role='tooltip'
          className='pointer-events-none fixed z-[32000] w-[300px] max-w-[calc(100vw-24px)] rounded-xl bg-slate-900 px-3.5 py-3 text-left text-xs font-medium leading-5 text-white shadow-xl'
          style={{left: position.left, top: position.top}}
        >
          {content}
          <span
            className={`absolute right-2 h-2.5 w-2.5 rotate-45 bg-slate-900 ${position.above ? '-bottom-1' : '-top-1'}`}
            aria-hidden='true'
          />
        </div>,
        document.body
      )}
    </>
  )
}
